import React, { useEffect, useRef, useState, useMemo, useContext } from "react";
import { Box, Grid, Typography, Modal } from "@mui/material";
import {
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../@shared/firebase/config";
import SpriteAnimator from "../components/gamePlay/SpriteAnimator";
import NeoButton from "../components/NeoButton";
import { useRoomsStore } from "../@shared/store/useRoomsStore";
import { useAuthStore } from "../@shared/store/useAuthStore";
import CharacterCard from "../components/CharacterCard";

import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useCardStore } from "../@shared/store/useCardsStore";
import { shuffle } from "../@shared/game/deckEngine";
import { Portal } from "@mui/material";
import {
  savePlayerDeckAndHand,
  updatePlayerPresence,
} from "../@shared/utils/roomFunctions";
import stackedDeck from "../@shared/assets/stacked_deck.png";
import {
  getDatabase,
  ref,
  onDisconnect,
  set,
  update,
  remove,
} from "firebase/database";
import { MainContext } from "../App";
import {
  ArrowBack,
  ArrowBackIos,
  ArrowForwardIos,
  CheckRounded,
  Close,
  LogoDevRounded,
  Logout,
  MenuRounded,
  Sms,
} from "@mui/icons-material";
import InfinityCard from "../components/InfinityCard";
import { yellow } from "@mui/material/colors";
import zIndex from "@mui/material/styles/zIndex";
import { applyBoosts, getAvailableBoosts } from "../@shared/utils/Utils";
import Toast from "../components/Toast";
import SettingsDrawer from "../components/SettingsDrawer";
import ChatDrawer from "../components/ChatDrawer";

const LobbyScreen = () => {
  const navigate = useNavigate();
  const {
    Theme,
    setError,
    setMessage,
    setScreenLoading,
    setGameScreenLoading,
  } = useContext(MainContext);
  const {
    currentRoom,
    subscribeToRoom,
    hasUnreadMessages,
    clearUnreadMessages,
    unreadCount,
    subscribeToChatThread,
    setChatDrawerOpen,
  } = useRoomsStore();
  const { userData } = useAuthStore();
  const { infinityCards, characterCards } = useCardStore();

  const currentPlayer = useMemo(() => {
    return currentRoom?.players.find((player) => player.id === userData.id);
  }, [currentRoom?.players, userData.id]);

  const allReady = currentRoom?.players.every((player) => player.ready);
  const botFillNumber = currentRoom?.botFill
    ? currentRoom?.amountOfPlayers - currentRoom?.players.length
    : 0;
  const timedTurns = currentRoom?.timedTurns ? "ON" : "OFF";
  const turnTime = currentRoom?.timedTurns && currentRoom?.turnTime;
  const [revealedPlayers, setRevealedPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [selectedBoosts, setSelectedBoosts] = useState({});
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [showDeckSelector, setShowDeckSelector] = useState(false);
  const [charIndex, setCharIndex] = useState(0);
  const [deckIndex, setDeckIndex] = useState(0);
  const [showDecks, setShowDecks] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [settingsVisible, setSettingsVisible] = useState(false);
  const spriteRefs = useRef({});

  useEffect(() => {
    if (!currentRoom?.id) return;
    const maybeUnsubscribe = subscribeToRoom(currentRoom?.id);
    return () => {
      if (typeof maybeUnsubscribe === "function") {
        maybeUnsubscribe();
      }
    };
  }, [currentRoom?.id, subscribeToRoom]);

  useEffect(() => {
    if (currentRoom?.players?.length) {
      setRevealedPlayers((prev) => {
        const newLength = currentRoom.players.length;
        const newState = [...prev];
        while (newState.length < newLength) {
          newState.push(true); // Mark new players as revealed immediately
        }
        return newState;
      });
    }
  }, [currentRoom?.players?.length]);

  useEffect(() => {
    const updatePresence = async () => {
      const dbRef = getDatabase();
      const presenceRef = ref(dbRef, `presence/${userData.id}`);
      await update(presenceRef, {
        inGameRoom: true,
        lastSeen: Date.now(),
        roomId: currentRoom.id,
      });
    };
    if (userData?.id && currentRoom?.id) {
      setScreenLoading(false);
      updatePresence();
    }
  }, [userData?.id, currentRoom?.id]);

  useEffect(() => {
    setSelectedBoosts({});
  }, [charIndex]);

  useEffect(() => {
    console.log(userData.decks[deckIndex]?.cards);
  }, [deckIndex]);

  useEffect(() => {
    if (currentRoom?.status === "match") {
      setGameScreenLoading(true);

      navigate(`/gameboard/${currentRoom?.id}/match`);
    }
  }, [currentRoom?.status]);

  const handleAnimationFinish = (index) => {
    setRevealedPlayers((prev) => {
      const copy = [...prev];
      copy[index] = true;
      return copy;
    });
  };

  const handleSelect = (selected) => {
    console.log("Selected:", selected);
    if (showDecks) {
      setSelectedDeck(selected);
      setSelectedCharacter(selected?.character);
      setShowDecks(true);
    } else {
      setSelectedCharacter(selected);
      setShowDecks(false);
    }
  };

  const makeBotPlayers = async () => {
    const bots = [];

    for (let i = 0; i < botFillNumber; i++) {
      const id = `bot_${i + 1}`; // Use unique string to prevent ID collision
      const shuffledDeck = shuffle(
        [...infinityCards]
        // .filter(
        //   (card) => !card.areaAffected.includes("Turn Loss")
        // )
      );
      const deck = shuffledDeck.slice(0, 35);
      const hand = shuffledDeck.slice(35, 40);

      let availChars = [...characterCards].filter(
        (c) =>
          c.rarity.toLowerCase() !== "legendary" &&
          c.rarity.toLowerCase() !== "epic"
      );

      const randomCharacter =
        availChars[Math.floor(Math.random() * availChars.length)];

      const botPlayer = {
        id,
        isBot: true,
        name: "BotPlayer " + (i + 1),
        character: {
          ...randomCharacter,
          health: Number(randomCharacter.health),
          attack: Number(randomCharacter.attack),
          defense: Number(randomCharacter.defense),
          accuracy: Number(randomCharacter.accuracy),
          startingHealth: Number(randomCharacter.health),
          startingDefense: Number(randomCharacter.defense),
          startingAttack: Number(randomCharacter.attack),
          startingAccuracy: Number(randomCharacter.accuracy),
          buffs: {
            attack: [],
            defense: [],
            health: [],
            accuracy: [],
          },
          isDead: false,
          stance: "defense",
        },
        ready: true,
      };

      // Save hand/deck externally
      await savePlayerDeckAndHand(currentRoom.id, id, { deck, hand });

      bots.push(botPlayer);
    }

    return bots;
  };

  const handleLeaveRoom = async () => {
    if (!currentRoom || !userData) return;

    const isOwner = currentRoom?.roomOwner === userData.id;
    const updatedPlayers = currentRoom?.players.filter(
      (player) => player.id !== userData.id
    );
    const updatedPlayerIds = currentRoom?.playerIds.filter(
      (id) => id !== userData.id
    );

    const roomRef = doc(db, "rooms", currentRoom?.id);

    try {
      const dbRef = getDatabase();
      const presenceRef = ref(dbRef, `presence/${userData.id}`);
      await update(presenceRef, {
        inGameRoom: false,
        lastSeen: Date.now(),
        roomId: null,
      });
      //   await remove(presenceRef);
      if (updatedPlayers.length === 0) {
        // 👋 Delete the room if this was the last player
        await deleteDoc(roomRef);
      } else {
        const newOwnerId = isOwner
          ? updatedPlayers[0].id
          : currentRoom?.roomOwner;

        await updateDoc(roomRef, {
          players: updatedPlayers,
          playerIds: updatedPlayerIds,
          roomOwner: newOwnerId,
        });
      }

      // ✅ Navigate back to Dashboard

      setScreenLoading(true);
      const timeout = setTimeout(() => {
        navigate("/world");
      }, 500); // small delay, but lets TransitionOverlay take over

      return () => clearTimeout(timeout);
    } catch (err) {
      console.error("Error while leaving room:", err);
    }
  };

  const handleKickPlayer = async (playerId) => {
    if (currentRoom?.roomOwner !== userData.id) return;

    const updatedPlayers = currentRoom?.players.filter(
      (p) => p.id !== playerId
    );
    const updatedPlayerIds = currentRoom?.playerIds.filter(
      (id) => id !== playerId
    );

    await updateDoc(doc(db, "rooms", currentRoom?.id), {
      players: updatedPlayers,
      playerIds: updatedPlayerIds,
    });

    const dbRef = getDatabase();
    const presenceRef = ref(
      dbRef,
      `rooms/${currentRoom.id}/presence/${playerId}`
    );
    await remove(presenceRef);

    setModalVisible(false);
  };

  const handleReady = async () => {
    console.log("Setting to Ready!");
    if (!currentRoom || !selectedDeck || !selectedCharacter) return;

    let shuffledDeck = shuffle(selectedDeck.cards);
    const fullDeck = [...shuffledDeck.slice(0, 35)];
    const hand = [...shuffledDeck.slice(35, 40)];

    const existingBoosts = selectedCharacter.boosts || {
      health: 0,
      attack: 0,
      defense: 0,
      accuracy: 0,
    };

    const newBoosts = selectedBoosts[selectedCharacter.id] || {
      health: 0,
      attack: 0,
      defense: 0,
      accuracy: 0,
    };

    // Combine existing and new boosts
    const combinedBoosts = {
      health: existingBoosts.health + newBoosts.health,
      attack: existingBoosts.attack + newBoosts.attack,
      defense: existingBoosts.defense + newBoosts.defense,
      accuracy: existingBoosts.accuracy + newBoosts.accuracy,
    };

    const totalBoostsUsed = Object.values(newBoosts).reduce((a, b) => a + b, 0);

    const boostedCharacter = {
      ...selectedCharacter,
      health: Number(selectedCharacter.health) + combinedBoosts.health,
      attack: Number(selectedCharacter.attack) + combinedBoosts.attack,
      defense: Number(selectedCharacter.defense) + combinedBoosts.defense,
      accuracy: Number(selectedCharacter.accuracy) + combinedBoosts.accuracy,
      startingHealth: Number(selectedCharacter.health),
      startingAttack: Number(selectedCharacter.attack),
      startingDefense: Number(selectedCharacter.defense),
      startingAccuracy: Number(selectedCharacter.accuracy),
      buffs: {
        attack: [],
        defense: [],
        accuracy: [],
        health: [],
      },
      boosts: combinedBoosts,
      amountOwned:
        selectedCharacter.amountOwned > totalBoostsUsed
          ? selectedCharacter.amountOwned - totalBoostsUsed
          : 1,
      stance: "defense",
      isDead: false,
    };

    const updatedPlayers = currentRoom?.players.map((p) =>
      p.id === userData.id
        ? {
            avatar: p.avatar,
            name: p.username,
            id: p.id,
            level: p.level,
            xp: p.xp,
            record: p.record,
            ready: true,
            character: boostedCharacter,
            isBot: false,
            seasonPass: p?.seasonPass?.active ? p?.seasonPass?.active : false,
          }
        : p
    );

    try {
      const roomRef = doc(db, "rooms", currentRoom?.id);
      await updateDoc(roomRef, { players: updatedPlayers });

      // 🧠 Save deck and hand to Firestore subcollections
      await savePlayerDeckAndHand(currentRoom.id, userData.id, {
        deck: fullDeck,
        hand,
      });

      console.log("✅ Deck and hand saved to Firestore");

      if (totalBoostsUsed > 0) {
        const characterRef = doc(
          db,
          "users",
          userData.id,
          "Character cards",
          selectedCharacter.id
        );

        await updateDoc(characterRef, {
          boosts: combinedBoosts,
          amountOwned:
            selectedCharacter.amountOwned > totalBoostsUsed
              ? selectedCharacter.amountOwned - totalBoostsUsed
              : 1,
        });

        console.log("✅ Boost data saved to Firestore character card");
      }

      setShowDeckSelector(false);
    } catch (err) {
      console.error("❌ Error in handleReady:", err);
    }
  };

  const createMatch = async () => {
    if (!currentRoom) return;

    console.log("Creating Match...");
    setGameScreenLoading(true);

    try {
      const botPlayers = await makeBotPlayers(currentRoom.id, botFillNumber);

      function shuffleArray(array) {
        const arr = [...array];
        for (let i = arr.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
      }

      const allPlayers = [...currentRoom.players, ...botPlayers];
      const shuffledPlayers = shuffleArray(allPlayers);

      const roomRef = doc(db, "rooms", currentRoom?.id);
      const matchRef = doc(db, "rooms", currentRoom.id, "matchData", "match");
      //   const resultsRef = doc(db, "rooms", roomId, "matchData", 'results');
      const matchData = {
        message: "Match has started!",
        amountOfPlayers: currentRoom.amountOfPlayers,
        botFill: currentRoom.botFill,
        currentPlayerTurn: -1,
        cpMovesLeft: 2,
        currentTurn: 1,
        eliminated: [],
        players: shuffledPlayers,
        status: "live",
        ranked: currentRoom.ranked || false,
        teams: currentRoom.teams || [],
        teamEnabled: (currentRoom.teams?.length ?? 0) > 0,
        timedTurns: currentRoom.timedTurns || false,
        turnStartTime: new Date().getTime(),
        turnTime: Number(currentRoom.turnTime) || null,
        winner: null,
        pve: currentRoom.pve || false,
      };

      // 1. Update Room status
      await updateDoc(roomRef, { status: "match" });

      // 2. Create the match
      await setDoc(matchRef, matchData);

      // 👇 Add after match is created
      const playerPointsRef = doc(
        db,
        "rooms",
        currentRoom.id,
        "matchData",
        "playerPoints"
      );

      const playerPointsData = {};
      const livePlayers = shuffledPlayers.filter((p) => !p.isBot);

      livePlayers.forEach((player) => {
        playerPointsData[player.id] = {
          damageDealtPoints: 0,
          eliminationPoints: 0,
          placementPoints: 0,
          playedCardPoints: 0,
          discardCardPoints: 0,
          tradeCardPoints: 0,
        };
      });

      await setDoc(playerPointsRef, playerPointsData);

      // 3. Update each player's Firestore user doc with currentRoomId
      const playersToUpdate = shuffledPlayers.filter((p) => !p.isBot);

      await Promise.all(
        playersToUpdate.map(async (player) => {
          const userRef = doc(db, "users", player.id);
          await updateDoc(userRef, {
            currentRoomId: currentRoom.id,
          });
        })
      );

      // 4. Update local state + navigate
      useRoomsStore.getState().setMatchData(matchData);
      useRoomsStore.getState().subscribeToMatch(currentRoom.id, "match");

      navigate(`/gameboard/${currentRoom.id}/match`, {
        roomId: currentRoom.id,
        matchId: "match",
      });
    } catch (err) {
      console.error("❌ Error creating match:", err);
    }
  };

  const getPlayerPosition = (index) => {
    const positions = [
      { top: "49%", left: "27%" },
      { top: "49%", left: "53%" },
      { top: "60%", left: "76%" },
      { top: "60%", left: "6%" },
      { top: "60%", left: "40%" },
      { top: "70%", left: "22%" },
      { top: "67%", left: "58%" },
      { top: "78%", left: "6%" },
      { top: "78%", left: "44%" },
      { top: "31%", left: "20%" },
      { top: "38%", left: "56%", height: 60 },
      { top: "38%", left: "80%", height: 60 },
    ];
    return positions[index] || { top: 400, left: 100 };
  };

  const renderPlayer = (player, index) => {
    const positionStyle = getPlayerPosition(index);
    const needsCropping = positionStyle.height !== undefined;
    const isRevealed = revealedPlayers[index];

    return (
      <Box
        key={player.id}
        onClick={() => {
          setSelectedPlayer(player);
          setModalVisible(true);
        }}
        sx={{
          ...styles.playerContainer,
          ...positionStyle,
          ...(needsCropping
            ? { overflow: "hidden", height: positionStyle.height }
            : {}),
        }}
      >
        <Box sx={styles.playerContent}>
          {player.ready && (
            <Box sx={styles.readyContainer}>
              <Typography sx={styles.readyText}>READY!!</Typography>
            </Box>
          )}
          {!isRevealed ? (
            <Box sx={styles.spriteContainer}>
              <SpriteAnimator
                ref={(el) => (spriteRefs.current[index] = el)}
                frameHeight={64}
                frameCount={6}
                fps={12}
                animationName="smokepuff1"
                onFinish={() => handleAnimationFinish(index)}
              />
            </Box>
          ) : (
            <>
              {needsCropping && (
                <Typography sx={{ ...styles.name, mb: 1, mt: 0 }}>
                  {player.username}
                </Typography>
              )}
              <Box
                component="img"
                src={
                  userData?.id === player.id
                    ? selectedCharacter?.imageUrl
                    : player?.character?.imageUrl
                    ? player?.character?.imageUrl
                    : "https://static-00.iconduck.com/assets.00/profile-circle-icon-512x512-zxne30hp.png"
                }
                alt="avatar"
                sx={{
                  ...styles.avatar,
                  mb: needsCropping ? -20 : 0,
                }}
              />
              {!needsCropping && (
                <Typography sx={styles.name}>{player.username}</Typography>
              )}
            </>
          )}
        </Box>
      </Box>
    );
  };

  const generatePlayer = async () => {
    if (!currentRoom) return;
    const newPlayer = {
      id: currentRoom.players.length + 1,
      name: "Player " + (currentRoom.players.length + 1),
      character: {
        imageUrl:
          "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/NFT%20Images%2F546546.png?alt=media&token=b831e028-472a-45cc-8584-7e6e5e8f08e0",
        name: "Toshi",
        rarity: "Rare",
        attack: 4,
        defense: 7,
        health: 8,
        accuracy: 6,
        buffs: {
          attack: Math.floor(Math.random() * 4),
          defense: 0,
          health: Math.floor(Math.random() * 4),
          accuracy: Math.floor(Math.random() * 4),
        },
        startingHealth: 8,
        startingDefense: 7,
        isDead: false,
      },
      deck: null,
      hand: [],
    };

    const roomRef = doc(db, "rooms", currentRoom.id);
    const updatedPlayers = [...currentRoom.players, newPlayer];
    try {
      await updateDoc(roomRef, { players: updatedPlayers });
    } catch (error) {
      console.error("Error adding player:", error);
    }
  };

  const uniqueCardsWithCounts = useMemo(() => {
    const cardMap = {};

    userData.decks[deckIndex]?.cards?.forEach((card) => {
      if (!cardMap[card.id]) {
        cardMap[card.id] = { ...card, count: 1 };
      } else {
        cardMap[card.id].count += 1;
      }
    });

    return Object.values(cardMap);
  }, [userData.decks, deckIndex]);

  if (!currentRoom) return null;

  return (
    <Box
      sx={{
        backgroundImage: {
          xs: `url(${require("../@shared/assets/lobbybg.png")})`,
          md: `url(${require("../@shared/assets/lobbyweb.png")})`,
        },
        backgroundSize: "cover",
        backgroundPosition: "bottom",
        width: "100%",
        height: "100%",
        position: "relative",
      }}
    >
      {!settingsVisible && (
        <Portal>
          <Box style={{ position: "fixed", top: 16, left: 16, zIndex: 2000 }}>
            <NeoButton
              isIcon={true}
              icon={<MenuRounded />}
              color={Theme.palette.tirciary.main}
              width={100}
              borderRadius={14}
              onClick={() => setSettingsVisible(true)}
            />
          </Box>
        </Portal>
      )}

      <ChatDrawer
        userData={userData}
        roomId={currentRoom?.id}
        visible={chatOpen}
        onClose={() => {
          setChatOpen(false);
          setChatDrawerOpen(false); // ✅ close
        }}
      />

      <SettingsDrawer
        open={settingsVisible}
        onClose={() => {
          setSettingsVisible(false);
        }}
        onclick={() => {
          handleLeaveRoom();
        }}
        screen={"lobby"}
        // sx={{ zIndex: 3000 }}
      />

      <Typography sx={{ ...styles.playerCount, top: { xs: "2%", md: "10%" } }}>
        Access Code: {currentRoom.accessCode}
      </Typography>
      <Typography
        sx={{
          fontSize: { xs: 15, md: 20 },
          fontWeight: 900,
          color: "#fff",
          position: "absolute",
          top: { xs: "5%", md: "13%" },
          left: { xs: "29.5%", md: "13%" },
        }}
      >
        {currentRoom.players.length} / {currentRoom.amountOfPlayers} Players
      </Typography>
      {botFillNumber > 0 && (
        <Typography sx={styles.botCount}>{botFillNumber} Bots</Typography>
      )}
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 500,
          color: "#fff",
          position: "absolute",
          top: { xs: "10.5%", md: "18%" },
          left: { xs: "29.5%", md: "13%" },
        }}
      >
        Timer: {timedTurns}
      </Typography>
      {currentRoom.timedTurns && (
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 500,
            color: "#fff",
            position: "absolute",
            top: { xs: "12%", md: "20%" },
            left: { xs: "29%", md: "13%" },
          }}
        >
          {turnTime} Seconds
        </Typography>
      )}

      {currentRoom.players.map((p, i) => renderPlayer(p, i))}

      {/* 
	   //! POSITION TEST BUTTON ====== DO NOT REMOVE!!! 
     <Box sx={styles.buttonWrapper}>
        <Typography sx={styles.button} onClick={generatePlayer}>
          + Add Player
        </Typography>
      </Box> */}

      {!showDeckSelector && (
        <Box
          sx={{
            ...styles.buttonWrapper,
            p: 0,
            backgroundColor: "transparent",
            width: "fit-content",
            top: "5%",
          }}
        >
          <NeoButton
            isIcon={true}
            icon={<Logout />}
            // width={100}
            color={Theme.palette.secondary.main}
            onClick={handleLeaveRoom}
          />
        </Box>
      )}

      {/* Action Buttons */}
      {!currentPlayer?.ready && !showDeckSelector && (
        <Box sx={styles.actionButtonsWrapper}>
          <Box sx={{ width: { xs: "50%", md: "30%", lg: "25%" } }}>
            <NeoButton
              text="Select Deck"
              width={100}
              color={Theme.palette.primary.main}
              onClick={() => setShowDeckSelector(true)}
            />
          </Box>
        </Box>
      )}

      {allReady &&
        currentRoom?.roomOwner === userData.id &&
        (currentRoom.amountOfPlayers === currentRoom.players.length ||
          !!currentRoom.botFill) && (
          <Box
            sx={{
              ...styles.actionButtonsWrapper,
              width: { xs: "70%", md: "50%", lg: "40%" },
            }}
          >
            <NeoButton
              text="START GAME!"
              width={100}
              color={Theme.palette.secondary.main}
              onClick={createMatch}
            />
          </Box>
        )}

      {/* View Character Card Modal */}
      <Modal open={modalVisible} onClose={() => setModalVisible(false)}>
        <Box sx={styles.modalOverlay}>
          <Box sx={styles.modalContent}>
            {currentRoom.roomOwner === userData.id &&
              selectedPlayer?.id !== userData.id && (
                <Box
                  sx={{
                    ...styles.modalButton,
                    position: "absolute",
                    right: -40,
                    top: -20,
                  }}
                  onClick={() => handleKickPlayer(selectedPlayer.id)}
                >
                  <NeoButton
                    text="Kick"
                    color="#da1d4e"
                    width={65}
                    onClick={() => handleKickPlayer(selectedPlayer.id)}
                  />
                </Box>
              )}
            <Box sx={styles.modalHeader}>
              <Box
                component="img"
                src={
                  selectedPlayer?.character?.imageUrl ||
                  "https://static-00.iconduck.com/assets.00/profile-circle-icon-512x512-zxne30hp.png"
                }
                alt="avatar"
                sx={styles.modalAvatar}
              />
              <Box sx={styles.modalInfo}>
                <Typography sx={styles.modalName}>
                  {selectedPlayer?.name}
                </Typography>
                <Box sx={styles.modalStatsRow}>
                  <Typography sx={styles.modalText}>
                    Wins: {selectedPlayer?.record?.wins ?? 0}
                  </Typography>
                  <Typography sx={styles.modalText}>
                    Losses: {selectedPlayer?.record?.losses ?? 0}
                  </Typography>
                  <Typography sx={styles.modalText}>
                    Level: {selectedPlayer?.level ?? 1}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={styles.modalButtons}>
              <Box sx={styles.modalButton}>
                <NeoButton text="Report" color="#da1d4e" width={80} />
              </Box>
              <Box sx={styles.modalButton}>
                <NeoButton
                  text="Add Friend"
                  color={Theme.palette.primary.main}
                  width={100}
                />
              </Box>
            </Box>
            <Box onClick={() => setModalVisible(false)}>
              <Typography sx={styles.modalClose}>Close</Typography>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* {showDeckSelector && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          sx={styles.deckSelector}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography sx={styles.selectorTitle}>
              {showDecks ? "My Decks" : "My Characters"}
            </Typography>
            <NeoButton
              isIcon
              icon={<Box component="span">X</Box>}
              color={Theme.palette.secondary.main}
              onClick={() => setShowDeckSelector(false)}
            />
          </Box>

          <Box sx={{ overflowX: "auto", display: "flex", gap: 2, mb: 2 }}>
            {(showDecks ? userData.decks : userData.nftCards).map(
              (item, index) => (
                <Box
                  key={index}
                  sx={styles.cardWrapper}
                  onClick={() => handleSelect(item)}
                >
                  {showDecks ? (
                    <>
                      <Box
                        component="img"
                        src={item.character?.imageUrl || item.imageUrl}
                        alt="deck"
                        sx={{
                          ...styles.deckImage,
                          borderWidth:
                            selectedDeck?.id === item.id ||
                            selectedCharacter?.id === item.id
                              ? 2
                              : 0,
                          borderColor: "#000",
                        }}
                      />
                      <Typography sx={styles.cardText}>{item?.name}</Typography>
                    </>
                  ) : (
                    <>
                      <Box
                        sx={{
                          ...styles.deckImage,
                          borderWidth:
                            selectedDeck?.id === item.id ||
                            selectedCharacter?.id === item.id
                              ? 2
                              : 0,
                          borderColor: "#000",
                          height: 120,
                          width: 120,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <CharacterCard
                          {...item}
                          containerHeight={100}
                          heightPer={100}
                        />
                      </Box>
                      <Typography sx={styles.cardText}>{item?.name}</Typography>
                    </>
                  )}
                </Box>
              )
            )}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <NeoButton
              text={showDecks ? "Show NFTs" : "Show Decks"}
              color={Theme.palette.secondary.main}
              width={46}
              onClick={() => setShowDeckSelector(!showDecks)}
            />
          </Box>
        </Box>
      )} */}

      {showDeckSelector && (
        <Modal
          open={showDeckSelector}
          sx={{ zIndex: Theme.zIndex.modal }}
          onClose={() => setShowDeckSelector(false)}
        >
          <Grid
            item
            container
            xs={12}
            sx={{
              height: "100dvh",
              position: "relative",
            }} //
          >
            <Toast timeTilClose={4000} onFinish={() => {}} />
            <Box
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 200,
                width: 42,
              }}
            >
              <NeoButton
                isIcon={true}
                icon={<Close />}
                color={Theme.palette.secondary.main}
                onClick={() => setShowDeckSelector(false)}
              />
            </Box>

            <Grid
              item
              container
              xs={12}
              sx={{
                height: { xs: "80%", md: "100%" },
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              {/* Character Selector */}
              <Grid
                item
                container
                xs={12}
                md={7}
                style={{ justifyContent: "center", alignItems: "center" }}
                sx={{ height: { xs: "60%", md: "80%" }, p: { xs: 2, md: 4 } }}
              >
                <Grid
                  item
                  container
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  xs={11}
                  sx={{
                    display: "flex",
                    height: "100%",
                    position: "relative",
                    top: { xs: "0%", md: "25%" },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: "300px", md: "75%" },
                      height: { xs: "40px", md: "10%" },
                      position: "relative",
                      backgroundColor: "#00000090",
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: 20, md: 40 },
                        fontWeight: "bold",
                        mb: 2,
                        color: "#fff",
                        width: "100%",
                        textAlign: "center",
                        position: "relative",
                        top: { xs: "10%", md: 0 },
                      }}
                    >
                      Choose Your Character
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "70%",
                    }}
                  >
                    {/* Left Arrow */}
                    <Box style={{ width: 42, zIndex: 100 }}>
                      <NeoButton
                        isIcon={true}
                        color={
                          charIndex === 0
                            ? Theme.palette.secondary.main
                            : Theme.palette.primary.main
                        }
                        icon={<ArrowBackIos style={{ paddingLeft: 10 }} />}
                        onClick={() => {
                          if (charIndex === 0) {
                            setCharIndex(userData.nftCards.length - 1);
                          } else {
                            setCharIndex(charIndex - 1);
                          }
                        }}
                        //   disabled={charIndex === 0}
                        // width={40}
                      />
                    </Box>

                    {/* Character Cards */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mx: 2,
                        height: "90%",
                      }}
                    >
                      {[charIndex - 1, charIndex, charIndex + 1].map(
                        (i, offsetIdx) => {
                          if (i < 0 || i >= userData.nftCards.length)
                            return (
                              <Box
                                key={i}
                                sx={{
                                  width: 100,
                                  height: 100,
                                  mx: {
                                    xs: offsetIdx === 1 ? 1 : -4.5,
                                    md: offsetIdx === 1 ? 1 : -0.5,
                                  },
                                }}
                              />
                            );

                          const character = userData.nftCards[i];
                          const attributes = [
                            {
                              name: "Health",
                              value: character.health,
                              icon: require("../@shared/assets/icons/health_icon.png"),
                            },
                            {
                              name: "Attack",
                              value: character.attack,
                              icon: require("../@shared/assets/icons/swords_icon2.png"),
                            },
                            {
                              name: "Defense",
                              value: character.defense,
                              icon: require("../@shared/assets/icons/sheild.png"),
                            },
                            {
                              name: "Accuracy",
                              value: character.accuracy,
                              icon: require("../@shared/assets/icons/cross-hair.png"),
                            },
                          ];

                          const isCenter = i === charIndex;

                          const boosts = selectedBoosts[character.id] || {};
                          const totalBoosted = Object.values(boosts).reduce(
                            (a, b) => a + b,
                            0
                          );

                          const boostsAvailable = getAvailableBoosts(
                            character,
                            selectedBoosts[character.id]
                          );

                          return (
                            <Box
                              key={character.id || i}
                              sx={{
                                mx: offsetIdx === 1 ? 1 : -2.5,
                                zIndex: isCenter ? 100 : 0,
                                opacity: isCenter ? 1 : 0.4,
                                transform: `scale(${isCenter ? 1.1 : 0.8})`,
                                transition: "all 0.3s ease-in-out",
                                height: "65%",
                                position: "relative",
                              }}
                            >
                              {isCenter && (
                                <img
                                  src={character.imageStart}
                                  style={{
                                    height: "auto",
                                    width: "100%",
                                    objectFit: "contain",
                                    position: "absolute",
                                    top: "-26%",
                                    right: "-60%",
                                    zIndex: 1,
                                  }}
                                />
                              )}

                              <CharacterCard
                                {...applyBoosts(character)}
                                containerHeight={
                                  window.innerWidth >= 900 ? 300 : 200
                                }
                                heightPer={80}
                                showAmtOwned={true}
                              />
                              {isCenter && (
                                <Box
                                  sx={{
                                    width: { xs: "300%", md: "150%" },
                                    height: { xs: "50px", md: "100px" },
                                    borderRadius: "10px",
                                    background: `${Theme.palette.primary.dark}`,
                                    padding: "2%",
                                    border: `4px solid ${Theme.palette.tirciary.dark}`,
                                    position: "absolute",
                                    bottom: { xs: "-25%", md: "-20%" },
                                    left: "50%",
                                    transform: "translate(-50%, 0%)",
                                    display: "flex",
                                    flexDirection: "column",
                                    // alignItems: "center",
                                    // justifyContent: "center",
                                  }}
                                >
                                  <Typography
                                    style={{
                                      textAlign: "center",
                                      fontWeight: 800,
                                      fontSize: 20,
                                      color: yellow[700],
                                      textShadow: ` -1px -1px 0 #000, 1px -1px 0 #000, -1px  1px 0 #000, 1px  1px 0 #000`,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {boostsAvailable}
                                    <img
                                      style={{
                                        height: 20,
                                        marginLeft: 3,
                                        marginRight: 3,
                                      }}
                                      src={require("../@shared/assets/icons/lightning_icon.png")}
                                    />
                                    BOOST
                                    {boostsAvailable !== 1 ? "S" : ""}{" "}
                                    <span
                                      style={{
                                        fontSize: 14,
                                        fontWeight: 700,
                                        marginLeft: 5,
                                      }}
                                    >
                                      Available
                                    </span>
                                  </Typography>
                                  <Box
                                    style={{
                                      width: "100%",
                                      display: "flex",
                                      justifyContent: "space-evenly",
                                    }}
                                  >
                                    {attributes.map((attribute, index) => (
                                      <motion.div
                                        whileHover={{
                                          scale: 1.1,
                                        }}
                                        whileTap={{
                                          scale: 0.9,
                                          opacity: 0.8,
                                        }}
                                        key={index}
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          marginTop: 5,
                                          cursor: "pointer",
                                          background:
                                            Theme.palette.tirciary.dark,
                                          padding: 5,
                                          borderRadius: 5,
                                          position: "relative",
                                        }}
                                        onClick={() => {
                                          const existingBoosts =
                                            character.boosts || {
                                              health: 0,
                                              attack: 0,
                                              defense: 0,
                                              accuracy: 0,
                                            };

                                          const existingBoostTotal =
                                            Object.values(
                                              existingBoosts
                                            ).reduce((a, b) => a + b, 0);
                                          const maxBoostsReached =
                                            totalBoosted + existingBoostTotal >=
                                            5;

                                          const existingAttack =
                                            Number(character.attack) +
                                            (existingBoosts.attack || 0);
                                          const existingAccuracy =
                                            Number(character.accuracy) +
                                            (existingBoosts.accuracy || 0);

                                          const pendingBoosts = selectedBoosts[
                                            character.id
                                          ] || {
                                            health: 0,
                                            attack: 0,
                                            defense: 0,
                                            accuracy: 0,
                                          };

                                          const pendingAttack =
                                            pendingBoosts.attack;
                                          const pendingAccuracy =
                                            pendingBoosts.accuracy;

                                          // Predict what total stats would be after this click
                                          const totalAttackIfBoosted =
                                            existingAttack +
                                            pendingAttack +
                                            (attribute.name === "Attack"
                                              ? 1
                                              : 0);

                                          const totalAccuracyIfBoosted =
                                            existingAccuracy +
                                            pendingAccuracy +
                                            (attribute.name === "Accuracy"
                                              ? 1
                                              : 0);

                                          const combinedTotal =
                                            totalAttackIfBoosted +
                                            totalAccuracyIfBoosted;

                                          // Rule 1: Accuracy max = 8
                                          if (
                                            attribute.name === "Accuracy" &&
                                            totalAccuracyIfBoosted > 8
                                          ) {
                                            setMessage(
                                              "⚠️ Accuracy cannot be boosted above 8."
                                            );
                                            setError(true);
                                            return;
                                          }

                                          // Rule 2: Attack + Accuracy max = 15
                                          if (combinedTotal > 15) {
                                            setMessage(
                                              "⚠️ Combined Attack and Accuracy cannot exceed 15."
                                            );
                                            setError(true);
                                            return;
                                          }

                                          // Global limits
                                          if (
                                            character.amountOwned <= 1 ||
                                            maxBoostsReached
                                          )
                                            return;

                                          // Add the boost
                                          setSelectedBoosts((prev) => {
                                            const current = prev[
                                              character.id
                                            ] || {
                                              health: 0,
                                              attack: 0,
                                              defense: 0,
                                              accuracy: 0,
                                            };

                                            const totalSelected =
                                              Object.values(current).reduce(
                                                (a, b) => a + b,
                                                0
                                              ) || 0;

                                            if (
                                              totalSelected >=
                                              character.amountOwned - 1
                                            )
                                              return prev;

                                            return {
                                              ...prev,
                                              [character.id]: {
                                                ...current,
                                                [attribute.name.toLowerCase()]:
                                                  current[
                                                    attribute.name.toLowerCase()
                                                  ] + 1,
                                              },
                                            };
                                          });
                                        }}
                                      >
                                        {selectedBoosts[character.id]?.[
                                          attribute.name.toLowerCase()
                                        ] > 0 && (
                                          <Box
                                            sx={{
                                              position: "absolute",
                                              top: -8,
                                              right: -8,
                                              backgroundColor: "#ffeb3b",
                                              color: "#000",
                                              borderRadius: "50%",
                                              width: 20,
                                              height: 20,
                                              fontSize: 12,
                                              fontWeight: "bold",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              zIndex: 10,
                                            }}
                                          >
                                            +
                                            {selectedBoosts[character.id]?.[
                                              attribute.name.toLowerCase()
                                            ] || 0}
                                          </Box>
                                        )}

                                        <div
                                          style={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            transform: "translate(-50%, -50%)",
                                            height: "100%",
                                            width: "100%",
                                            zIndex: 1,
                                          }}
                                        />
                                        <img
                                          src={attribute.icon}
                                          style={{
                                            width: 15,
                                            height: 15,
                                            marginRight: 5,
                                          }}
                                        />
                                        <Typography
                                          style={{
                                            fontSize: 14,
                                            fontWeight: 600,
                                            color: "#fff",
                                            textShadow: "1px 1px 0 #000",
                                          }}
                                        >
                                          {attribute.value}
                                        </Typography>
                                      </motion.div>
                                    ))}

                                    {totalBoosted > 0 && (
                                      <Box
                                        sx={{
                                          mt: 2,
                                          display: "flex",
                                          justifyContent: "center",
                                          position: "absolute",
                                          bottom: { xs: "230%", md: "-20%" },
                                          left: { xs: "10%", md: "50%" },
                                          transform: "translate(-50%, 0%)",
                                        }}
                                      >
                                        <NeoButton
                                          text="Undo Boosts"
                                          color={Theme.palette.secondary.main}
                                          onClick={() => setSelectedBoosts({})}
                                          width={120}
                                        />
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          );
                        }
                      )}
                    </Box>

                    {/* Right Arrow */}
                    <Box style={{ width: 42, zIndex: 100 }}>
                      <NeoButton
                        isIcon={true}
                        color={
                          charIndex === userData.nftCards.length - 1
                            ? Theme.palette.secondary.main
                            : Theme.palette.primary.main
                        }
                        icon={<ArrowForwardIos style={{}} />}
                        onClick={() => {
                          if (charIndex === userData.nftCards.length - 1) {
                            setCharIndex(0);
                          } else {
                            setCharIndex(charIndex + 1);
                          }
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Confirm Character */}
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      position: "relative",
                      top: { xs: "5%", md: 0 },
                    }}
                  >
                    <NeoButton
                      startAdorn={
                        userData.nftCards[charIndex]?.id ===
                          selectedCharacter?.id && (
                          <CheckRounded
                            style={{
                              fontSize: 30,
                              color: "green",
                              marginRight: 5,
                            }}
                          />
                        )
                      }
                      text={
                        userData.nftCards[charIndex]?.id ===
                        selectedCharacter?.id
                          ? "Selected"
                          : selectedCharacter
                          ? "Update Character"
                          : "Confirm Character"
                      }
                      color={
                        userData.nftCards[charIndex]?.id ===
                        selectedCharacter?.id
                          ? Theme.palette.tirciary.main
                          : selectedCharacter
                          ? Theme.palette.secondary.main
                          : Theme.palette.primary.main
                      }
                      onClick={() => {
                        const selected = userData.nftCards[charIndex];
                        setSelectedCharacter(selected);

                        setShowDecks(true); // Switch to deck view
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>

              {/* Deck Selector */}
              <Grid
                item
                container
                xs={12}
                md={5}
                style={{ alignItems: "center" }}
                sx={{ height: { xs: "50%", md: "80%" }, p: { xs: 2, md: 4 } }}
              >
                <Grid
                  item
                  container
                  alignItems="center"
                  justifyContent="center"
                  xs={11}
                  sx={{
                    height: "100%",
                    position: "relative",
                    top: { xs: "0%", md: "25%" },
                    left: { xs: "8%", md: "0%" },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: "250px", md: "95%" },
                      height: { xs: "40px", md: "10%" },
                      position: "relative",
                      backgroundColor: "#00000090",
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: 20, md: 40 },
                        fontWeight: "bold",
                        mb: 1,
                        color: "#fff",
                        width: "100%",
                        textAlign: "center",
                      }}
                    >
                      Choose Your Deck
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "30%",
                    }}
                  >
                    {/* Left Arrow */}
                    <Box style={{ width: 42 }}>
                      <NeoButton
                        isIcon={true}
                        color={
                          deckIndex === 0
                            ? Theme.palette.secondary.main
                            : Theme.palette.primary.main
                        }
                        icon={<ArrowBackIos style={{ paddingLeft: 10 }} />}
                        onClick={() => {
                          if (deckIndex === 0) {
                            setDeckIndex(userData.decks.length - 1);
                          } else {
                            setDeckIndex(deckIndex - 1);
                          }
                        }}
                        //   disabled={charIndex === 0}
                        // width={40}
                      />
                    </Box>

                    {/* Deck Boxes */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mx: 2,
                        height: "100%",
                      }}
                    >
                      {[deckIndex - 1, deckIndex, deckIndex + 1].map(
                        (i, offsetIdx) => {
                          if (i < 0 || i >= userData.decks.length)
                            return (
                              <Box
                                key={i}
                                sx={{
                                  width: { xs: 90, md: 100 },
                                  height: { xs: 120, md: 100 },
                                  mx: offsetIdx === 1 ? 1 : -4.5,
                                }}
                              />
                            );

                          const deck = userData.decks[i];
                          const isCenter = i === deckIndex;

                          return (
                            <Box
                              key={deck.id || i}
                              sx={{
                                mx: offsetIdx === 1 ? 1 : -3.5,
                                zIndex: isCenter ? 100 : 0,
                                opacity: isCenter ? 1 : 0.4,
                                transform: `scale(${isCenter ? 1.1 : 0.65})`,
                                transition: "all 0.3s ease-in-out",
                                height: { xs: 120, md: 160 },
                                width: { xs: 90, md: 120 },
                                border: "2px solid",
                                borderColor: isCenter
                                  ? Theme.palette.primary.main
                                  : "#888",
                                borderRadius: 2,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#111",
                                color: "#fff",
                                fontWeight: "bold",
                                textAlign: "center",
                                fontSize: 14,
                                position: "relative",
                              }}
                            >
                              <img
                                src={require(`../@shared/assets/deck-box.png`)}
                                style={{
                                  width: "70%",
                                  height: "70%",
                                  objectFit: "contain",
                                  zIndex: 1,
                                }}
                              />
                              <Box
                                style={{
                                  position: "absolute",
                                  top: "45%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  width: "64%",
                                  height: "35%",
                                  backgroundColor:
                                    deck?.color ?? Theme.palette.primary.main,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: 2,
                                  zIndex: 0,
                                  opacity: isCenter ? 1 : 0,
                                  transition: "opacity 0.3s ease-in-out",
                                }}
                              />

                              {deck.name || "Unnamed Deck"}
                            </Box>
                          );
                        }
                      )}
                    </Box>

                    {/* Right Arrow */}
                    <Box style={{ width: 42 }}>
                      <NeoButton
                        isIcon={true}
                        color={
                          deckIndex === userData.decks.length - 1
                            ? Theme.palette.secondary.main
                            : Theme.palette.primary.main
                        }
                        icon={<ArrowForwardIos style={{}} />}
                        onClick={() => {
                          if (deckIndex === userData.decks.length - 1) {
                            setDeckIndex(0);
                          } else {
                            setDeckIndex(deckIndex + 1);
                          }
                        }}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: { xs: "none", md: "block" },
                      height: { sx: "100px", md: "200px" },
                      width: "60%",
                      overflowY: "scroll",
                      scrollbarWidth: "none",
                      pr: 1,
                    }}
                  >
                    {uniqueCardsWithCounts.map((card, index) => (
                      <Box
                        key={index}
                        style={{
                          height: "80px",
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          marginBottom: 4,
                          overflow: "hidden",
                          backgroundColor: "#ffffff40",
                          borderRadius: 10,
                          position: "relative",
                        }}
                      >
                        <Box
                          style={{
                            position: "relative",
                            height: "100%",
                            marginLeft: 8,
                          }}
                        >
                          <img
                            src={card.imageUrl}
                            alt=""
                            style={{ height: "80%", width: "auto" }}
                          />
                          <Box
                            style={{
                              position: "absolute",
                              top: 2,
                              right: 2,
                              backgroundColor: "#000000aa",
                              color: "#fff",
                              borderRadius: "50%",
                              width: 24,
                              height: 24,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 14,
                              fontWeight: "bold",
                            }}
                          >
                            {card.count}
                          </Box>
                        </Box>

                        <Box
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            width: "74%",
                            marginLeft: 8,
                          }}
                        >
                          <Typography
                            style={{
                              fontSize: 14,
                              fontWeight: 900,
                              color: "#fff",
                            }}
                          >
                            {card.name}
                          </Typography>
                          <Typography style={{ fontSize: 12, color: "#fff" }}>
                            {card.effect}
                          </Typography>
                          <Typography style={{ fontSize: 12, color: "#fff" }}>
                            {card.areaAffected?.join(", ")}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  {/* Confirm Deck */}
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <NeoButton
                      startAdorn={
                        userData.decks[deckIndex]?.id === selectedDeck?.id && (
                          <CheckRounded
                            style={{
                              fontSize: 30,
                              color: "green",
                              marginRight: 5,
                            }}
                          />
                        )
                      }
                      text={
                        userData.decks[deckIndex]?.id === selectedDeck?.id
                          ? "Selected"
                          : selectedDeck
                          ? "Change Deck"
                          : "Confirm Deck"
                      }
                      color={
                        userData.decks[deckIndex]?.id === selectedDeck?.id
                          ? Theme.palette.tirciary.main
                          : selectedDeck
                          ? Theme.palette.secondary.main
                          : Theme.palette.primary.main
                      }
                      onClick={() => {
                        const selected = userData.decks[deckIndex];
                        setSelectedDeck(selected);
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            {selectedCharacter && selectedDeck && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: { xs: 40, md: 24 },
                  left: { xs: "52%", md: "50%" },
                  transform: "translateX(-50%)",
                  zIndex: 200,
                  width: "160px",
                }}
              >
                <NeoButton
                  text="Ready!"
                  color={Theme.palette.secondary.main}
                  onClick={handleReady}
                  width={100}
                />
              </Box>
            )}
          </Grid>
        </Modal>
      )}

      {!chatOpen && (
        <Box
          sx={{
            position: "absolute",
            bottom: "15px",
            left: "15px",
            zIndex: 9998,
          }}
        >
          <Box
            style={{
              width: "100%",
              height: "100%",
              position: "relative",
            }}
          >
            {hasUnreadMessages && <Box sx={styles.unread} />}
            {/* <Badge
							  badgeContent={unreadCount > 0 ? unreadCount : null}
							  color="error"
							  sx={{
								"& .MuiBadge-badge": {
								  top: 2,
								  right: 2,
								  border: "2px solid #000",
								  fontSize: "0.75rem",
								  height: "16px",
								  minWidth: "16px",
								},
							  }}
							> */}
            <NeoButton
              isIcon={true}
              icon={<Sms size={30} color={Theme.palette.text.main} />}
              color={Theme.palette.primary.main}
              onClick={() => {
                setChatOpen(true);
                setChatDrawerOpen(true); // ✅ open
                clearUnreadMessages();
              }}
            />
            {/* </Badge> */}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const styles = {
  background: {
    backgroundSize: "cover",
    backgroundPosition: "center",

    backgroundRepeat: "no-repeat",
    width: "100%",
    height: "100%",
    position: "relative",
  },
  playerCount: {
    fontSize: { xs: 15, md: 20 },
    fontWeight: 900,
    color: "#fff",
    position: "absolute",
    top: "13%",
    left: { xs: "29%", md: "13%" },
  },
  botCount: {
    fontSize: 16,
    fontWeight: 500,
    color: "#fff",
    position: "absolute",
    top: { xs: "8%", md: "16%" },
    left: { xs: "29.5%", md: "13%" },
  },
  avatar: {
    width: 76,
    height: 76,
  },
  name: {
    color: "#fff",
    fontSize: 11,
    fontWeight: "bold",
    textAlign: "center",
    mt: 1,
  },
  playerContainer: {
    position: "absolute",
    width: 76,
    height: 90,
    display: "flex",
    alignItems: "center",
  },
  playerContent: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  readyContainer: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#00000070",
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 5,
    zIndex: 100,
    width: "80%",
    height: "80%",
  },
  readyText: {
    color: "#fff",
    fontSize: 16,
    transform: "rotate(-8deg)",
    fontWeight: 900,
    textShadow: "2px 2px #000",
  },
  spriteContainer: {
    position: "absolute",
    width: 64,
    height: 64,
    overflow: "hidden",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  sprite: {
    width: 64,
    height: 64,
  },
  buttonWrapper: {
    position: "absolute",
    top: 50,
    right: 20,
    backgroundColor: "#222",
    borderRadius: 12,
    p: 1,
  },
  actionButtonsWrapper: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 10,
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-evenly",
    zIndex: 1000,
  },
  deckButtonWrapper: {
    width: "48%",
  },
  deckSelector: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "40%",
    backgroundColor: "#fff",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    p: 2,
    zIndex: 100,
  },
  deckImage: {
    width: 120,
    height: 120,
    borderRadius: 10,
    mb: 1,
  },
  cardWrapper: {
    display: "flex",
    alignItems: "center",
    mr: 2,
  },
  cardText: {
    fontSize: 12,
    fontWeight: "bold",
    mt: 1,
  },
  selectorTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: "#333",
  },
  button: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: 16,
  },
  modalOverlay: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0,0,0,0.6)",
    width: "100%",
    height: "100vh",
  },
  modalContent: {
    width: "85%",
    backgroundColor: "#16116e",
    p: 2,
    borderRadius: 2,
    overflowY: "auto",
  },
  modalHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    mb: 2,
  },
  modalAvatar: {
    width: 64,
    height: 64,
    borderRadius: 32,
    mr: 2,
  },
  modalInfo: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  modalName: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#fff",
    mb: 1,
  },
  modalStatsRow: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
  },
  modalText: {
    fontSize: 14,
    color: "#ccc",
    mr: 1,
  },
  modalButtons: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    mt: 2,
  },
  modalButton: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalClose: {
    color: "#888",
    fontSize: 14,
    mt: 2,
  },
  unread: {
    position: "absolute",
    top: 3,
    right: 0,
    backgroundColor: "#FF0000",
    padding: "5px",
    borderRadius: "10px",
    borderWidth: "4px",
    borderColor: "#000",
    color: "#FFFFFF",
    width: "12px",
    height: "12px",
    zIndex: 1000,
  },
};

export default LobbyScreen;
